import { useRef, useState, useMemo } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Stars } from '@react-three/drei';
import * as THREE from 'three';
import { portfolioProjects, PortfolioProject } from '../data/portfolioProjects';
import ProjectMarker from './ProjectMarker';

interface InteractiveGlobeProps {
  onProjectClick: (project: PortfolioProject) => void;
  onProjectHover: (project: PortfolioProject | null) => void;
}

export default function InteractiveGlobe({ onProjectClick, onProjectHover }: InteractiveGlobeProps) {
  const globeRef = useRef<THREE.Group>(null);
  const [hoveredProject, setHoveredProject] = useState<PortfolioProject | null>(null);
  const { camera } = useThree();

  // Create Earth-like texture with continents
  const earthTexture = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 512;
    const ctx = canvas.getContext('2d')!;

    // Ocean base
    ctx.fillStyle = '#1e40af';
    ctx.fillRect(0, 0, 1024, 512);

    // Helper function to draw continents
    const drawContinent = (x: number, y: number, width: number, height: number, color: string) => {
      ctx.fillStyle = color;
      ctx.beginPath();
      // Draw irregular continent shapes
      ctx.moveTo(x, y);
      for (let i = 0; i < 8; i++) {
        const angle = (i / 8) * Math.PI * 2;
        const radiusX = width * (0.5 + Math.random() * 0.3);
        const radiusY = height * (0.5 + Math.random() * 0.3);
        ctx.lineTo(
          x + Math.cos(angle) * radiusX,
          y + Math.sin(angle) * radiusY
        );
      }
      ctx.closePath();
      ctx.fill();
    };

    // Draw continents in green/brown tones
    const landColor = '#10b981'; // Green for land
    const landColorDark = '#059669'; // Darker green

    // Africa-ish (center-right)
    drawContinent(550, 280, 80, 120, landColor);
    drawContinent(520, 250, 60, 80, landColorDark);
    
    // Europe-ish (center-top)
    drawContinent(480, 180, 90, 60, landColor);
    drawContinent(500, 160, 50, 40, landColorDark);
    
    // Asia-ish (right)
    drawContinent(700, 200, 140, 100, landColor);
    drawContinent(720, 230, 100, 80, landColorDark);
    
    // Americas-ish (left)
    drawContinent(180, 200, 70, 90, landColor);
    drawContinent(200, 280, 80, 110, landColorDark);
    drawContinent(160, 180, 50, 70, landColor);
    
    // Australia-ish (right-bottom)
    drawContinent(780, 360, 70, 50, landColor);
    
    // Add some islands
    for (let i = 0; i < 20; i++) {
      drawContinent(
        Math.random() * 1024,
        Math.random() * 512,
        Math.random() * 20 + 10,
        Math.random() * 20 + 10,
        i % 2 === 0 ? landColor : landColorDark
      );
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    return texture;
  }, []);

  // Slow auto-rotation
  useFrame(() => {
    if (globeRef.current && !hoveredProject) {
      globeRef.current.rotation.y += 0.001;
    }
  });

    // Project locations (latitude, longitude) - positioned on landmasses
  const projectLocations: { project: PortfolioProject; lat: number; lon: number }[] = [
    { project: portfolioProjects[0], lat: 40, lon: -80 },      // Americas (North) - Watershed HR
    { project: portfolioProjects[1], lat: 52, lon: 10 },       // Europe - Two Souls Co
    { project: portfolioProjects[2], lat: 35, lon: 135 },      // Asia (East) - Countable
    { project: portfolioProjects[3], lat: -25, lon: 135 },     // Australia - Wright Compliance
    { project: portfolioProjects[4], lat: 5, lon: 20 },        // Africa (Central) - Maryborough Whistle Stop
    { project: portfolioProjects[5], lat: 25, lon: -100 },     // North America (Mexico) - Table to Text
  ];

  const handleMarkerClick = (project: PortfolioProject, position: THREE.Vector3) => {
    // Zoom camera to marker
    const targetPosition = position.clone().multiplyScalar(1.8);
    camera.position.lerp(targetPosition, 0.1);
    onProjectClick(project);
  };

  return (
    <>
      <color attach="background" args={['#0a0e1a']} />
      <ambientLight intensity={0.3} />
      <directionalLight position={[5, 3, 5]} intensity={1} />
      <pointLight position={[-5, -3, -5]} intensity={0.5} color="#4299e1" />

      {/* Stars background */}
      <Stars radius={100} depth={50} count={5000} factor={4} fade speed={1} />

      {/* Globe with markers as children so they rotate together */}
      <group ref={globeRef}>
        {/* Globe */}
        <mesh>
          <sphereGeometry args={[1, 64, 64]} />
          <meshStandardMaterial
            map={earthTexture}
            roughness={0.7}
            metalness={0.1}
          />
          
          {/* Atmosphere glow */}
          <mesh scale={1.05}>
            <sphereGeometry args={[1, 64, 64]} />
            <meshBasicMaterial
              color="#60a5fa"
              transparent
              opacity={0.15}
              side={THREE.BackSide}
            />
          </mesh>
        </mesh>

        {/* Project markers - children of globe group */}
        {projectLocations.map(({ project, lat, lon }) => (
          <ProjectMarker
            key={project.id}
            project={project}
            latitude={lat}
            longitude={lon}
            onClick={handleMarkerClick}
            onHover={(isHovered) => {
              const hoveredProj = isHovered ? project : null;
              setHoveredProject(hoveredProj);
              onProjectHover(hoveredProj);
            }}
          />
        ))}
      </group>

      {/* OrbitControls for interaction */}
      <OrbitControls
        enableZoom={true}
        enablePan={false}
        minDistance={1.5}
        maxDistance={5}
        zoomSpeed={0.8}
      />
    </>
  );
}
